import React from 'react';
import moment from 'moment';
import { Virtuoso } from 'react-virtuoso';

import { Spinner } from 'src/components/basic';
import BaseComponent from 'src/components/BaseComponent';
import Scrollbar from 'src/components/MCScrollbar';

import { remCss, withRouter, lang, permissions } from 'src/helpers';

import model from './model';
import ChatInput from './ChatInput';
import Message from './Message';

import { popupModel } from 'src/components/complex';
import AddTreatment from 'src/components/treatments/Create';
import Typing from 'src/components/basic/Typing';

const { ACTIONS } = permissions;

const calendarFormats = {
  sameDay: '[Today]',
  lastDay: '[Yesterday]',
  lastWeek: '[Last] dddd',
  sameElse: 'MMMM DD, YYYY'
};

class Chat extends BaseComponent {
  constructor(props) {
    super(props);
    this.chatRef = React.createRef();
    this.scrollbars = React.createRef();
  }

  componentDidMount() {
    model.subscribe(this.subscription);
    model.on('openContact', this.focusInput);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
    model.cancel('openContact', this.focusInput);
  }

  onEditMessage = (item) => {
    if (item.msgType === 'treatment') {
      const { treatmentsModel } = this.props;
      popupModel.setActivePopup(ACTIONS.TREATMENTS_ADD_BUTTON.key, {
        active: true,
        component: AddTreatment,
        props: {
          editItem: item,
          partnerId: model.getState().partnerId,
          afterSave: (error, data, success) => {
            if (success) {
              data = treatmentsModel.mapItem(data);
              treatmentsModel.filterItem(data);
            }
          }
        }
      });
    } else {
      model.onEditMessage(item);
      this.inputRef.focus();
    }
  };

  onRemoveMessage = (item, forBoth) => {
    const { treatmentsModel } = this.props;
    if (item.msgType === 'treatment') {
      treatmentsModel.remove(item.data.treatmentId, item);
      model.removeItem(item.id, true, null, true);
    } else {
      model.removeMessage(item.id, forBoth);
    }
  };

  focusInput = () => {
    this.inputRef && this.inputRef.focus();
  };

  renderVirtuoso() {
    const items = model.items().slice().reverse();
    const partnerId = model.getState().partnerId;
    const loading = model.refreshing();
    let searchQuery = model.listInput('search');
    let noResults = model.noResults();
    const auth = model.getUser();

    let { name, lastRead, partnerLastRead } = model.profile();
    const selectedMessages = model.selectedMessages();
    return (
      <Virtuoso
        key={'message-chat-' + partnerId}
        components={{
          Header: () => (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              {noResults && !searchQuery && <div>{lang.t('No results yet')}</div>}
              {noResults && searchQuery && <div>{lang.t('Nothing found')}</div>}
              {loading && <Spinner />}
            </div>
          ),
          Footer: () =>
            model.state.uploading ? (
              <div className={'mc-text-msg mc-right'} style={{ maxWidth: remCss(235) }}>
                <div className={'mc-content'}>
                  <Spinner />
                </div>
              </div>
            ) : null
        }}
        ref={this.chatRef}
        data={items}
        defaultItemHeight={18}
        initialTopMostItemIndex={model.listLimit - 1}
        endReached={() => {
          if (model.hasMore() && !loading && !model.state.uploading) {
            model.loadMore();
          }
        }}
        itemContent={(index, item) => {
          let timeMessage = moment(item.date).calendar(calendarFormats);
          if (index === 0) {
            this.lastDate = timeMessage;
          } else if (this.lastDate === timeMessage) {
            timeMessage = null;
          } else {
            this.lastDate = timeMessage;
          }
          return (
            <Message
              key={item.id || item.tempId}
              item={item}
              lastRead={lastRead}
              partnerLastRead={partnerLastRead}
              counter={item.counter}
              imageLength={item.media && item.media.length}
              media={item.media}
              timeMessage={timeMessage}
              own={item.own}
              partnerName={name}
              auth={auth}
              quoted={item.quoted}
              forwarded={item.forwarded}
              text={item.text}
              edited={item.edited}
              showSelect={!!selectedMessages.length}
              selected={selectedMessages.map((s) => s.id).includes(item.id)}
              // onClickMessage={model.onSelectMessage}
              onEditMessage={this.onEditMessage.bind(this, item)}
              onRemoveMessage={this.onRemoveMessage.bind(this, item)}
            />
          );
        }}
      />
    );
  }

  renderScrollbar() {
    let items = model.items();
    const partnerId = model.getState().partnerId;
    const loading = model.refreshing();
    const auth = model.getUser();

    let { name, lastRead, partnerLastRead } = model.profile();
    const selectedMessages = model.selectedMessages();
    return (
      <Scrollbar
        scrollRef={(el) => (this.scrollbars = el)}
        loading={loading}
        hasMore={model.hasMore()}
        loadMore={model.loadMore}
        inverted
        keyIdentify={partnerId}
        data={items}
        dataLength={items.length}
        // fullSpinner={true}
        listContainerClass='mc-chat-list'
        endMessage='No messages yet'
        footer={() =>
          model.state.uploading ? (
            <div className={'mc-text-msg mc-right'} style={{ maxWidth: remCss(235) }}>
              <div className={'mc-content'}>
                <Spinner />
              </div>
            </div>
          ) : null
        }
        renderItem={({ item, index }) => {
          let timeMessage = moment(item.date).calendar(calendarFormats);
          if (index === 0) {
            this.lastDate = timeMessage;
          } else if (this.lastDate === timeMessage) {
            timeMessage = null;
          } else {
            this.lastDate = timeMessage;
          }
          return (
            <Message
              key={item.id || item.tempId}
              item={item}
              lastRead={lastRead}
              partnerLastRead={partnerLastRead}
              counter={item.counter}
              imageLength={item.media && item.media.length}
              files={item.files}
              media={item.media}
              timeMessage={timeMessage}
              own={item.own}
              partnerName={name}
              auth={auth}
              quoted={item.quoted}
              forwarded={item.forwarded}
              text={item.text}
              edited={item.edited}
              showSelect={!!selectedMessages.length}
              selected={selectedMessages.map((s) => s.id).includes(item.id)}
              // onClickMessage={model.onSelectMessage}
              onEditMessage={this.onEditMessage.bind(this, item)}
              onRemoveMessage={this.onRemoveMessage.bind(this, item)}
            />
          );
        }}
      />
    );
  }

  renderTicketInfo = () => {
    let ticket = model.getState().ticket;
    let daysLeft = moment(ticket.endDate).diff(Date.now(), 'days');
    let messagesLeft = ticket.type !== 'appointment' && ticket.maxMessages - ticket.messages;
    let result = lang.t('exp. ') + daysLeft + ' days; ';
    if (ticket.type !== 'appointment') result += messagesLeft + ' ' + lang.t('msg left');
    return <div className='mc-label-416 mc-chat-ticket-info'>{result}</div>;
  };

  render() {
    let { isPartnerTyping } = model.profile();
    let ticket = model.getState().ticket;
    let useScrollbar = true;
    return (
      <>
        <div className={'mc-chat'}>
          {!!ticket &&
            model.isChatAvailable() &&
            ticket.type !== 'appointment' &&
            this.renderTicketInfo()}
          <div className={'mc-grow'} />
          {!useScrollbar && this.renderVirtuoso()}
          {useScrollbar && this.renderScrollbar()}
        </div>
        <Typing active={isPartnerTyping} />
        <ChatInput
          inputRef={(ref) => (this.inputRef = ref)}
          placeholder='Type your message...'
          small
          value={model.listGet('input')}
          originalMessage={model.listGet('originalMessage')}
          files={model.listGet('files')}
          rerender={model.listGet('updated')}
          preview={model.listGet('preview')}
          previewUrl={model.listGet('previewUrl')}
          previewing={model.listGet('previewing')}
          onChange={model.onChangeInput}
          onSendMessage={model.sendMessage}
          onFiles={model.listHandleFiles.bind(model, 'files')}
          onRemoveFiles={model.listRemoveFiles.bind(model, 'files')}
          onCancelPreview={model.onCancelPreview}
          onRecord={model.onRecord}
          clearSelectedMessage={model.clearSelectedMessage}
          onInputMsgFocus={this.props.onInputMsgFocus}
        />
      </>
    );
  }
}

export default withRouter(Chat);
